import { useCallback, useEffect, useRef, useState } from 'react';

import type { ImageSuggestionsPageSummary } from '@/lib/blocks/image-suggestion-context';
import { getFinishOutputObject, getFinishOutputText } from '@/lib/mastra/stream';
import { RequestContext } from '@/lib/mastra/types';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from '@/lib/utils';
import { getImageSuggestionsPageSummary } from '@/stores/page-editor-store';
import type { ImageSuggestionTarget } from '@/types/image';
import { useMastraAgent } from './use-mastra-agent';
import type { AbortableMastraClient } from './use-mastra-client';

const IMAGE_SUGGESTIONS_AGENT_ID = 'imageSuggestions';
const CACHE_KEY_PREFIX = 'image-suggestions:';
const FNV1A_32_OFFSET_BASIS = 0x811c9dc5;
const FNV1A_32_PRIME = 0x01000193;

interface CacheEntry {
  suggestions: string[];
  contentHash: string;
  timestamp: number;
}

const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function buildStorageKey(pageId: string, contentHash: string): string {
  return `${CACHE_KEY_PREFIX}${pageId}:${contentHash}`;
}

function readCache(pageId: string, contentHash: string): CacheEntry | null {
  try {
    const key = buildStorageKey(pageId, contentHash);
    const raw = getLocalStorage(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CacheEntry;
    if (
      !parsed ||
      !Array.isArray(parsed.suggestions) ||
      !parsed.suggestions.every((s) => typeof s === 'string') ||
      typeof parsed.contentHash !== 'string' ||
      typeof parsed.timestamp !== 'number'
    ) {
      return null;
    }

    if (Date.now() - parsed.timestamp > CACHE_TTL_MS) {
      removeLocalStorage(key);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function writeCache(pageId: string, contentHash: string, entry: CacheEntry): void {
  try {
    setLocalStorage(buildStorageKey(pageId, contentHash), JSON.stringify(entry));
  } catch {
    // ignore
  }
}

function cleanupExpiredCacheEntries(): void {
  try {
    if (typeof globalThis.localStorage === 'undefined') return;

    const keys: string[] = [];
    for (let i = 0; i < globalThis.localStorage.length; i += 1) {
      const key = globalThis.localStorage.key(i);
      if (key?.startsWith(CACHE_KEY_PREFIX)) {
        keys.push(key);
      }
    }

    const now = Date.now();

    for (const key of keys) {
      const raw = getLocalStorage(key);
      if (!raw) {
        removeLocalStorage(key);
        continue;
      }

      try {
        const parsed = JSON.parse(raw) as Partial<CacheEntry>;
        const timestamp = typeof parsed?.timestamp === 'number' ? parsed.timestamp : null;
        if (timestamp === null || now - timestamp > CACHE_TTL_MS) {
          removeLocalStorage(key);
        }
      } catch {
        removeLocalStorage(key);
      }
    }
  } catch {
    // ignore cleanup failures
  }
}

// Card text is part of the hash, so each card gets its own cache entry and
// editing a card invalidates only that card's suggestions.
function buildContentSummary(summary: ImageSuggestionsPageSummary | null): string {
  if (!summary) return '';
  return JSON.stringify({
    sections: summary.sections.map((s) => ({
      sectionTemplateCode: s.sectionTemplateCode,
      variant: s.variant ?? null,
      textContent: s.textContent ?? null,
    })),
    card: summary.card?.textContent ?? null,
  });
}

function fallbackHash(input: string): string {
  let hash = FNV1A_32_OFFSET_BASIS;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, FNV1A_32_PRIME);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function buildContentHash(pageSummary: ImageSuggestionsPageSummary | null): Promise<string> {
  const summary = buildContentSummary(pageSummary);
  if (!summary) return 'empty';

  try {
    if (globalThis.crypto?.subtle) {
      const data = new TextEncoder().encode(summary);
      const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
      return `sha256:${toHex(digest)}`;
    }
  } catch {
    // fall through
  }

  return `fallback:${fallbackHash(summary)}`;
}

function normalizeSuggestions(suggestions: unknown[]): string[] {
  const deduped = new Set<string>();

  for (const item of suggestions) {
    if (typeof item !== 'string') continue;
    const normalized = item.trim();
    if (!normalized) continue;
    deduped.add(normalized);
  }

  return Array.from(deduped);
}

function parseSuggestionsFromUnknown(value: unknown): string[] {
  if (Array.isArray(value)) {
    return normalizeSuggestions(value);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      return parseSuggestionsFromUnknown(JSON.parse(trimmed));
    } catch {
      return [];
    }
  }

  if (value && typeof value === 'object') {
    const suggestions = (value as { suggestions?: unknown }).suggestions;
    if (Array.isArray(suggestions)) {
      return normalizeSuggestions(suggestions);
    }
  }

  return [];
}

interface UseImageSuggestionsOptions {
  pageId: string;
  sectionId?: string;
  target?: ImageSuggestionTarget;
  enabled: boolean;
  mastraClient: AbortableMastraClient;
}

interface UseImageSuggestionsReturn {
  suggestions: string[];
  isLoading: boolean;
}

export function useImageSuggestions({
  pageId,
  sectionId,
  target,
  enabled,
  mastraClient,
}: UseImageSuggestionsOptions): UseImageSuggestionsReturn {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const enabledRef = useRef(enabled);
  const requestRunIdRef = useRef(0);

  const { sendMessage, stop } = useMastraAgent({
    agentId: IMAGE_SUGGESTIONS_AGENT_ID,
    mastraClient,
  });

  const fetchSuggestions = useCallback(async () => {
    if (!pageId || hasFetchedRef.current || !enabledRef.current) return;
    const runId = ++requestRunIdRef.current;
    const pageSummary = getImageSuggestionsPageSummary(pageId, sectionId, target);
    const currentHash = await buildContentHash(pageSummary);
    if (!enabledRef.current || runId !== requestRunIdRef.current) return;
    const cached = readCache(pageId, currentHash);

    if (cached && cached.contentHash === currentHash) {
      if (!enabledRef.current || runId !== requestRunIdRef.current) return;
      setSuggestions(cached.suggestions);
      hasFetchedRef.current = true;
      return;
    }

    if (!enabledRef.current || runId !== requestRunIdRef.current) return;
    setIsLoading(true);
    setSuggestions([]);

    let raw = '';
    let structuredSuggestions: string[] = [];
    let completed = false;

    try {
      const requestContext = new RequestContext();
      if (pageSummary) {
        requestContext.set('pageSummary', pageSummary);
      }

      await sendMessage('Generate image suggestions for this page.', {
        concurrency: 'replace',
        streamOptions: {
          requestContext,
        },
        onChunk: (chunk) => {
          if (chunk.type === 'object' || chunk.type === 'object-result') {
            const fromObject = parseSuggestionsFromUnknown(chunk.object);
            if (fromObject.length > 0) {
              structuredSuggestions = fromObject;
            }
            return;
          }

          if (chunk.type === 'finish') {
            const fromFinishObject = parseSuggestionsFromUnknown(
              getFinishOutputObject(chunk.payload),
            );
            if (fromFinishObject.length > 0) {
              structuredSuggestions = fromFinishObject;
              return;
            }

            const finishText = getFinishOutputText(chunk.payload);
            if (!raw.trim() && finishText.trim()) {
              raw = finishText;
            }
            return;
          }

          if (chunk.type === 'text-delta' && chunk.payload?.text) {
            const delta = typeof chunk.payload.text === 'string' ? chunk.payload.text : '';
            if (delta) raw += delta;
          }
        },
      });

      const fallbackFromRaw = parseSuggestionsFromUnknown(raw);
      const finalSuggestions =
        structuredSuggestions.length > 0 ? structuredSuggestions : fallbackFromRaw;
      if (!enabledRef.current || runId !== requestRunIdRef.current) return;

      if (finalSuggestions.length > 0) {
        setSuggestions(finalSuggestions);
        writeCache(pageId, currentHash, {
          suggestions: finalSuggestions,
          contentHash: currentHash,
          timestamp: Date.now(),
        });
      }

      completed = finalSuggestions.length > 0;
    } catch {
      // Agent call fail — allow retry while still enabled.
      if (enabledRef.current && runId === requestRunIdRef.current) {
        hasFetchedRef.current = false;
      }
    } finally {
      if (completed && enabledRef.current && runId === requestRunIdRef.current) {
        hasFetchedRef.current = true;
      }
      if (enabledRef.current && runId === requestRunIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [pageId, sectionId, target, sendMessage]);

  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) {
      requestRunIdRef.current += 1;
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) {
      cleanupExpiredCacheEntries();
      hasFetchedRef.current = false;
      void fetchSuggestions();
    } else {
      setSuggestions([]);
      setIsLoading(false);
      hasFetchedRef.current = false;
    }

    return () => {
      stop();
    };
  }, [enabled, fetchSuggestions, stop]);

  return { suggestions, isLoading };
}
